import { api } from './client';
import type { ShirtColor, ShirtCut, Size } from '../types';

export type AdminSize = {
  id: number;
  productId: string;
  cut: ShirtCut;
  size: Size;
  displayOrder: number;
  createdAt: string;
  deletedAt: string | null;
};

export type AdminProductImage = {
  id: number;
  key: string;
  url: string | null;
  displayOrder: number;
};

export type AdminProduct = {
  id: string;
  color: ShirtColor;
  name: string;
  description: string;
  priceGrosze: number;
  material: string;
  printing: string;
  images: AdminProductImage[];
  imageUrl: string | null;
  displayOrder: number;
  createdAt: string;
  deletedAt: string | null;
  sizes: AdminSize[];
};

type RawAdminProduct = {
  id: string;
  color: ShirtColor;
  name: string;
  description: string;
  price_grosze?: number;
  priceGrosze?: number;
  material: string;
  printing: string;
  images?: Array<{
    id: number;
    key: string;
    url: string | null;
    displayOrder?: number;
    display_order?: number;
  }>;
  imageUrl: string | null;
  display_order?: number;
  displayOrder?: number;
  created_at?: string;
  createdAt?: string;
  deleted_at?: string | null;
  deletedAt?: string | null;
  sizes: Array<{
    id: number;
    product_id?: string;
    productId?: string;
    cut: ShirtCut;
    size: Size;
    display_order?: number;
    displayOrder?: number;
    created_at?: string;
    createdAt?: string;
    deleted_at?: string | null;
    deletedAt?: string | null;
  }>;
};

function normalize(raw: RawAdminProduct): AdminProduct {
  return {
    id: raw.id,
    color: raw.color,
    name: raw.name,
    description: raw.description,
    priceGrosze: raw.priceGrosze ?? raw.price_grosze ?? 0,
    material: raw.material,
    printing: raw.printing,
    images: (raw.images ?? []).map((img) => ({
      id: img.id,
      key: img.key,
      url: img.url,
      displayOrder: img.displayOrder ?? img.display_order ?? 0,
    })),
    imageUrl: raw.imageUrl ?? null,
    displayOrder: raw.displayOrder ?? raw.display_order ?? 0,
    createdAt: raw.createdAt ?? raw.created_at ?? '',
    deletedAt: raw.deletedAt ?? raw.deleted_at ?? null,
    sizes: raw.sizes.map((s) => ({
      id: s.id,
      productId: s.productId ?? s.product_id ?? raw.id,
      cut: s.cut,
      size: s.size,
      displayOrder: s.displayOrder ?? s.display_order ?? 0,
      createdAt: s.createdAt ?? s.created_at ?? '',
      deletedAt: s.deletedAt ?? s.deleted_at ?? null,
    })),
  };
}

export async function listAdminProducts(): Promise<AdminProduct[]> {
  const res = await api.get<{ products: RawAdminProduct[] }>('/admin/products');
  return res.products.map(normalize);
}

export type ProductDraft = {
  id?: string;
  color: ShirtColor;
  name: string;
  description?: string;
  price_grosze: number;
  material?: string;
  printing?: string;
  display_order?: number;
};

export const createProduct = (body: ProductDraft) =>
  api.post<{ ok: true; id: string }>('/admin/products', body);

export const updateProduct = (id: string, body: Partial<ProductDraft>) =>
  api.patch<{ ok: true }>(`/admin/products/${id}`, body);

export const softDeleteProduct = (id: string) =>
  api.del<{ ok: true }>(`/admin/products/${id}`);

export const restoreProduct = (id: string) =>
  api.post<{ ok: true }>(`/admin/products/${id}/restore`, {});

export const replaceProductSizes = (
  id: string,
  sizes: Array<{ cut: ShirtCut; size: Size }>,
) =>
  api.put<{ ok: true; sizes: AdminSize[] }>(`/admin/products/${id}/sizes`, { sizes });

export const requestProductImageUpload = (id: string, contentType: string) =>
  api.post<{ upload_url: string; key: string }>(`/admin/products/${id}/image-upload`, {
    content_type: contentType,
  });

export const replaceProductImages = (id: string, keys: string[]) =>
  api.put<{ ok: true; images: AdminProductImage[] }>(`/admin/products/${id}/images`, {
    images: keys.map((key) => ({ key })),
  });

export const PRODUCT_IMAGE_LIMIT = 3;
